import { Component, inject, model, resource, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BbitUnit, IBbitI18n, IBbitSelectDataRow, IBbitSerializedCurrency } from '@bbitgmbh/bbit.utils';
import { BbitOnlinePaymentService, BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { CreditTableComponent } from '../credit-table/credit-table.component';
import { CreditTopUpOverlayRef } from './credit-top-up-overlay.ref';

@Component({
  selector: 'app-credit-top-up-overlay',
  imports: [LoadingComponent, ReactiveFormsModule, FormsModule, NgIcon, I18nPipe, CreditTableComponent],
  templateUrl: './credit-top-up-overlay.component.html',
  styleUrl: './credit-top-up-overlay.component.scss',
})
export class CreditTopUpOverlayComponent {
  private _overlayRef = inject(CreditTopUpOverlayRef);

  private _onlinePayment = inject(BbitOnlinePaymentService);
  private _website = inject(BbitWebsiteService);
  public priceResult = resource<
    { canBeSold: boolean; text: IBbitI18n },
    { amount: number | undefined; paymentMethod: string | undefined }
  >({
    params: () => ({ amount: this.topUpCredits(), paymentMethod: this.selectedPaymentMethod() }),
    loader: async ({ params }) => {
      const amount = params.amount || 0;
      if (amount <= 0) {
        this.disableButton.set(true);
        return { canBeSold: false, text: { key: 'pleaseSetAQuantity' } };
      }
      if (!params.paymentMethod) {
        this.disableButton.set(true);
        return { canBeSold: false, text: { key: 'pleaseSelectAPaymentMethod' } };
      }
      const data = await this._call({ amount, paymentMethod: params.paymentMethod });
      this.disableButton.set(false);
      return { canBeSold: true, text: BbitUnit.from(data.priceInclVat).toFormattedAmount() };
    },
  });
  public topUpCredits = model<number | undefined>();
  public selectedPaymentMethod = model<string>();
  public paymentMethods = signal<IBbitSelectDataRow[]>([]);
  public disableButton = signal(true);

  private async _call({
    amount,
    finalize,
    paymentMethod,
  }: {
    amount: number;
    finalize?: boolean;
    paymentMethod?: string;
  }): Promise<{
    priceInclVat: IBbitSerializedCurrency;
    isOnlinePayment: boolean;
    invoiceId: string;
  }> {
    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/creditTopUp`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: JSON.stringify({ amount, finalize, paymentMethod }),
    });
    if (!response.ok) {
      throw new Error('call failed');
    }
    return await response.json();
  }

  async ngOnInit(): Promise<void> {
    const metaResponse = await fetch(`${this._website.getBlApiUrl()}/placeBooking/meta`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
    });
    const meta = await metaResponse.json();
    this.paymentMethods.set(meta.allowedCreditTopUpPaymentMethods);
    this.selectedPaymentMethod.set(meta.allowedCreditTopUpPaymentMethods?.[0]?._id);
  }

  async buy(): Promise<void> {
    if ((this.topUpCredits() || 0) <= 0 || !this.priceResult.value()?.canBeSold) {
      return;
    }
    this.disableButton.set(true);
    try {
      const result = await this._call({
        amount: this.topUpCredits() || 0,
        finalize: true,
        paymentMethod: this.selectedPaymentMethod(),
      });

      if (result.isOnlinePayment) {
        if (result.invoiceId) {
          this._onlinePayment.pay({
            invoice: result.invoiceId,
            redirectUrl: `${window.location.origin}/shop/order-result`,
          });
          return;
        }
      }
      this.disableButton.set(false);
      this._overlayRef.close(true);
    } catch (e) {
      console.error(e);
    }
  }

  close(): void {
    this._overlayRef.close();
  }
}
